import React, { useEffect, useState } from 'react';
import { notificationAPI } from '../services/api.ts';
import { Layout } from '../components/Layout.tsx';
import {
  Alert,
  Button,
  Input,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { Layers, Plus, Search } from 'lucide-react';
import { EmptyState, PageHeader } from '../components/app/index.ts';
import { toast } from '../lib/toast.ts';
import { useDropzone } from 'react-dropzone';
import { uploadToCloudinary } from '../services/index.tsx';

export const Notifications: React.FC = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !message) {
      setError('Title and message are required');
      return;
    }
    e.preventDefault();
    setIsLoading(true);

    let uploadImage;

    if (file) {
      const upload = await uploadToCloudinary(file, 'notifications');
      if (upload) {
        uploadImage = upload;
      }
    }

    try {
      const response = await notificationAPI.send({
        title: title,
        message: message,
        url: uploadImage,
      });

      if (response.success) {
        setTitle('');
        setMessage('');
        setError('');
        toast.success('Notification sent successfully');
        setIsModalOpen(false);
        getNotifications();
      } else {
        setError('Failed to send notification');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send notification');
    } finally {
      setIsLoading(false);
    }
  };

  const getNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.getAll();
      setLoading(false);
      setNotifications(response.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
      // pdf
      'application/pdf': ['.pdf'],
      // docx
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
    },
  });

  useEffect(() => {
    getNotifications();
  }, []);

  const filteredNotifications = notifications.filter((item) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      item.title?.toLowerCase().includes(q) ||
      item.body?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <PageHeader
        title="Notifications"
        description="Send notifications to your users in real-time."
        actions={
          <>
            <TextInput
              type="search"
              placeholder="Search by title or message..."
              leftSection={<Search size={16} />}
              w={{ base: '100%', sm: 288 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
            />
            <Button
              leftSection={<Plus size={16} />}
              onClick={() => setIsModalOpen(true)}
            >
              Add Notifications
            </Button>
          </>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        {loading ? (
          <Stack align="center" gap="xs" py="xl">
            <Loader />
            <Text c="dimmed">Loading Notifications...</Text>
          </Stack>
        ) : filteredNotifications.length > 0 ? (
          <Table.ScrollContainer minWidth={700}>
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Message</Table.Th>
                  <Table.Th>Timestamp</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredNotifications.map((config) => (
                  <Table.Tr key={config.id}>
                    <Table.Td maw={320} style={{ wordBreak: 'break-word' }}>
                      {config.title}
                    </Table.Td>
                    <Table.Td>{config.body}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        {new Date(config.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : (
          <EmptyState
            icon={<Layers size={28} />}
            title={searchTerm ? 'No matching notifications' : 'No Notifications'}
            description={
              searchTerm
                ? 'Try a different search term.'
                : 'Get started by adding a notification.'
            }
          />
        )}
      </Paper>

      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}

            <TextInput
              label="Title"
              placeholder="Enter your title"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />

            <Textarea
              label="Message"
              placeholder="Enter your message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.currentTarget.value)}
            />

            <Input.Wrapper label="Upload Image (Optional)">
              <Paper
                {...getRootProps()}
                withBorder
                p="md"
                radius="md"
                ta="center"
                style={{ cursor: 'pointer', borderStyle: 'dashed' }}
              >
                <input {...getInputProps()} />
                <Text size="sm" c="dimmed">
                  {file
                    ? file.name
                    : 'Drag and drop a file here, or click to select a file'}
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  Supported formats: .png, .jpg, .jpeg, .pdf, .docx, .xlsx,
                  .pptx
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  Max size: 10MB
                </Text>
              </Paper>
            </Input.Wrapper>

            <Button type="submit" fullWidth loading={isLoading}>
              Send
            </Button>
          </Stack>
        </form>
      </Modal>
    </Layout>
  );
};
